import currency from 'currency.js';
import { CareType } from '@/types/care_type';
import {
  companionServicesOptions,
  CompanionServicesOptions,
  petServicesOptions,
  PetServicesOptions,
} from '../../apps/sitter/signup/profile/components/__types__/ServiceTypes';

type CareTypeFormKey = 'babysitter' | 'nanny' | 'housekeeping' | 'petCare' | 'companionCare';

function getCareTypeForm(state: SitterOnboardingState) {
  const { careType } = state;
  if (!careType) return null;

  const form = state[careType as CareTypeFormKey];
  return form ?? null;
}

// this interface is in the form that graphQL expects
interface SitterOnboardingState {
  careType: string;
  address: {
    addressLine1: string;
    addressLine2: string;
    city: string;
    stateCode: string;
    postalCode: string;
  };
  babysitter: {
    yearsExperience: number | null;
    informalYearsExperience: number | null;
    ageGroups: Array<string>;
    accessToTransportation: boolean;
    lightHousekeeping: boolean;
    willingToDriveChildren: boolean;
    willingToRunErrands: boolean;
    homeworkHelp: boolean;
    laundry: boolean;
    mealPreparation: boolean;
    pottyTraining: boolean;
    sickChildren: boolean;
    specialNeeds: boolean;
    twins: boolean;
    qualifications: Array<string>;
    distanceWillingToTravel: number | null;
    maxNumberOfChildren: number | null;
    comfortableWithPets: boolean;
    paymentRate: {
      min: number | null;
      max: number | null;
    };
    bio: string;
  };
  nanny: {
    yearsExperience: number | null;
    informalYearsExperience: number | null;
    ageGroups: Array<string>;
    accessToTransportation: boolean;
    lightHousekeeping: boolean;
    willingToDriveChildren: boolean;
    willingToRunErrands: boolean;
    homeworkHelp: boolean;
    laundry: boolean;
    mealPreparation: boolean;
    pottyTraining: boolean;
    sickChildren: boolean;
    specialNeeds: boolean;
    twins: boolean;
    qualifications: Array<string>;
    distanceWillingToTravel: number | null;
    maxNumberOfChildren: number | null;
    comfortableWithPets: boolean;
    paymentRate: {
      min: number | null;
      max: number | null;
    };
    bio: string;
  };
  housekeeping: {
    yearsExperience: number | null;
    experience: Array<string>;
    services: Array<string>;
    distanceWillingToTravel: number | null;
    comfortableWithPets: boolean;
    paymentRate: {
      min: number | null;
      max: number | null;
    };
    bio: string;
  };
  petCare: {
    yearsExperience: number | null;
    experience: Array<string>;
    experienceOther: string;
    qualifications: Array<string>;
    distanceWillingToTravel: number | null;
    serviceRates: PetServicesOptions;
    bio: string;
    maxNumberOfPets: number | null;
  };
  companionCare: {
    yearsExperience: number | null;
    ageGroups: Array<string>;
    experience: Array<string>;
    qualifications: Array<string>;
    distanceWillingToTravel: number | null;
    serviceRates: CompanionServicesOptions;
    comfortableWithPets: boolean;
    bio: string;
  };
}

const state: SitterOnboardingState = {
  careType: '',
  address: {
    addressLine1: '',
    addressLine2: '',
    city: '',
    stateCode: '',
    postalCode: '',
  },
  babysitter: {
    yearsExperience: null,
    informalYearsExperience: null,
    ageGroups: [],
    qualifications: [],
    distanceWillingToTravel: null,
    maxNumberOfChildren: null,
    comfortableWithPets: false,
    accessToTransportation: false,
    lightHousekeeping: false,
    willingToDriveChildren: false,
    willingToRunErrands: false,
    homeworkHelp: false,
    laundry: false,
    mealPreparation: false,
    pottyTraining: false,
    sickChildren: false,
    specialNeeds: false,
    twins: false,
    paymentRate: {
      min: null,
      max: null,
    },
    bio: '',
  },
  nanny: {
    yearsExperience: null,
    informalYearsExperience: null,
    ageGroups: [],
    qualifications: [],
    distanceWillingToTravel: null,
    maxNumberOfChildren: null,
    comfortableWithPets: false,
    accessToTransportation: false,
    lightHousekeeping: false,
    willingToDriveChildren: false,
    willingToRunErrands: false,
    homeworkHelp: false,
    laundry: false,
    mealPreparation: false,
    pottyTraining: false,
    sickChildren: false,
    specialNeeds: false,
    twins: false,
    paymentRate: {
      min: null,
      max: null,
    },
    bio: '',
  },
  housekeeping: {
    yearsExperience: null,
    experience: [],
    services: [],
    distanceWillingToTravel: null,
    comfortableWithPets: false,
    paymentRate: {
      min: null,
      max: null,
    },
    bio: '',
  },
  petCare: {
    yearsExperience: null,
    experience: [],
    experienceOther: '',
    qualifications: [],
    distanceWillingToTravel: null,
    maxNumberOfPets: null,
    serviceRates: petServicesOptions,
    bio: '',
  },
  companionCare: {
    yearsExperience: null,
    ageGroups: [],
    experience: [],
    qualifications: [],
    distanceWillingToTravel: null,
    serviceRates: companionServicesOptions,
    comfortableWithPets: false,
    bio: '',
  },
};

const mutations = {
  SET_CARE_TYPE(state, careType) {
    state.careType = careType;
  },
  UPDATE_FORM(state, payload) {
    state[state.careType][payload.key] = payload.value;
  },
  UPDATE_ADDRESS(state, payload) {
    state.address[payload.key] = payload.value;
  },
  UPDATE_PAYMENT_RATE(state, payload) {
    state[state.careType].paymentRate[payload.key] = payload.value;
  },
  UPDATE_SERVICE_RATE(state, payload) {
    state[state.careType].serviceRates[payload.service].rate = payload.rate;
  },
  SELECT_SERVICE(state, payload) {
    state[state.careType].serviceRates[payload.service].selected = payload.selected;
  },
};

const actions = {
  setCareType({ commit }, careType) {
    commit('SET_CARE_TYPE', careType);
  },
  updateAddress({ commit }, payload) {
    commit('UPDATE_ADDRESS', payload);
  },
  updateForm({ commit }, payload) {
    commit('UPDATE_FORM', payload);
  },
  updatePaymentRate({ commit }, payload) {
    commit('UPDATE_PAYMENT_RATE', payload);
  },
  selectService({ commit }, payload) {
    commit('SELECT_SERVICE', payload);
  },
  updateServiceRate({ commit }, payload) {
    commit('UPDATE_SERVICE_RATE', payload);
  },
};

const getters = {
  sitterCareTypes: () => Object.values(CareType).filter((c) => c !== CareType.SPECIAL_NEEDS),
  address: (state) => state.address,
  careType: (state) => state.careType,
  megaForm: (state) => getCareTypeForm(state),
  getFormField: (state) => (fieldName) => getCareTypeForm(state)?.[fieldName],
  minPaymentRate: (state) => {
    const form = getCareTypeForm(state) as { paymentRate?: { min: number | null } } | null;
    return form?.paymentRate?.min ?? null;
  },
  maxPaymentRate: (state) => {
    const form = getCareTypeForm(state) as { paymentRate?: { max: number | null } } | null;
    return form?.paymentRate?.max ?? null;
  },
  yearsExperience: (state) => getCareTypeForm(state)?.yearsExperience ?? null,
  selectedServiceRates: (state) => {
    const form = getCareTypeForm(state) as { serviceRates?: PetServicesOptions | CompanionServicesOptions } | null;
    const serviceRates = form?.serviceRates;
    if (!serviceRates) return [];

    const selectedServiceRates = Object.keys(serviceRates)
      .filter((service) => serviceRates[service].selected) // Filter by selected services
      .map((selectedService) => {
        const { service, rate } = serviceRates[selectedService]; // Get service and rate from each selected service
        const rateInCents = currency(rate).intValue; // Convert rate to cents
        return { service, rate: rateInCents }; // Create objects with only service type and rate in cents
      });
    return selectedServiceRates; // GraphQL expects an array of objects with a service string and a rate
  },
};

export default {
  state,
  mutations,
  actions,
  getters,
};
